import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth, UserRole } from './AuthContext';
import { timesheetService } from '../services/timesheetService';
import { leaveRequestService } from '../services/leaveRequestService';
import { expenseClaimsService } from '../services/expenseClaimsService';
import { getTenantRequestsPendingCount } from '../services/auth.service';
import { notificationService } from '../services/notificationService';

interface ApprovalCountsContextType {
  pendingTimesheetCount: number;
  pendingLeaveCount: number;
  pendingExpenseCount: number;
  pendingTenantRequestCount: number;
  refreshCounts: () => Promise<void>;
}

const ApprovalCountsContext = createContext<ApprovalCountsContextType | undefined>(undefined);

export const ApprovalCountsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [pendingTimesheetCount, setPendingTimesheetCount] = useState(0);
  const [pendingLeaveCount, setPendingLeaveCount] = useState(0);
  const [pendingExpenseCount, setPendingExpenseCount] = useState(0);
  const [pendingTenantRequestCount, setPendingTenantRequestCount] = useState(0);

  const isManagerOrAdmin = user?.role && [UserRole.MANAGER, UserRole.ADMIN, UserRole.SUPER_ADMIN].includes(user.role);
  const canSeeApprovalFlows = user?.role && [UserRole.MANAGER, UserRole.ACCOUNTANT, UserRole.ADMIN, UserRole.SUPER_ADMIN].includes(user.role);
  const isAdminOrSuperAdmin = user?.role && [UserRole.ADMIN, UserRole.SUPER_ADMIN].includes(user.role);

  const refreshCounts = useCallback(async () => {
    if (!canSeeApprovalFlows) return;

    try {
      const [timesheetData, leaveData, expenseData] = await Promise.all([
        isManagerOrAdmin ? timesheetService.getPendingCount() : Promise.resolve({ count: 0 }),
        leaveRequestService.getPendingCount(),
        expenseClaimsService.getPendingCount(),
      ]);
      setPendingTimesheetCount(timesheetData.count);
      setPendingLeaveCount(leaveData.count);
      setPendingExpenseCount(expenseData.count);
    } catch (error) {
      console.error('Failed to refresh approval counts:', error);
    }

    if (isAdminOrSuperAdmin) {
      try {
        const tenantRequestData = await getTenantRequestsPendingCount();
        setPendingTenantRequestCount(tenantRequestData.count);
      } catch (error) {
        console.error('Failed to refresh tenant request count:', error);
      }
    }
  }, [canSeeApprovalFlows, isManagerOrAdmin, isAdminOrSuperAdmin]);

  // Initial fetch
  useEffect(() => {
    refreshCounts();
  }, [refreshCounts]);

  // Refresh counts when notifications arrive (debounced)
  useEffect(() => {
    if (!canSeeApprovalFlows) return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    const listener = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        refreshCounts();
        timer = null;
      }, 700);
    };

    const unsubscribe = notificationService.subscribe(() => listener());
    return () => {
      if (timer) clearTimeout(timer);
      unsubscribe();
    };
  }, [canSeeApprovalFlows, refreshCounts]);

  return (
    <ApprovalCountsContext.Provider
      value={{
        pendingTimesheetCount,
        pendingLeaveCount,
        pendingExpenseCount,
        pendingTenantRequestCount,
        refreshCounts,
      }}
    >
      {children}
    </ApprovalCountsContext.Provider>
  );
};

export const useApprovalCounts = () => {
  const context = useContext(ApprovalCountsContext);
  if (!context) {
    throw new Error('useApprovalCounts must be used within ApprovalCountsProvider');
  }
  return context;
};
